import 'select2';

import 'bootstrap/dist/js/bootstrap.min.js';
import 'bootstrap4-toggle/js/bootstrap4-toggle.min.js';
import 'notyf/notyf.min.js';
import './bootstrap';
import './packages/app.js';

const INITIAL_DOCUMENT_BRAND = document.documentElement.getAttribute('data-ui-brand') || 'autoflow';
import { initTheme } from './Utils/theme.js';
import { initBayhiveFavicon } from './Utils/bayhiveFavicon.js';
import { syncDocumentUiBrand } from './themes/primevue/index.js';
import { activeBrand } from './themes/primevue/brandState.js';
import { selectPageTheme, updatePageTheme } from './themes/primevue/shopTheme.js';
import dataTableOverflow from './Directives/ShopManagement/dataTableOverflow.js';
import { installBayhiveFormErrorNavigation } from './ShopManagement/formErrorNavigation.js';
import { installEmbeddedAutoflowDialogSizing } from './ShopManagement/embeddedAutoflow/dialogSizing.js';
import { createBayhivePrimeVuePassThrough } from './themes/primevue/bayhivePassThrough.js';
import { installAdminHistoryTracker } from './BayHiveAdmin/historyGuard.js';
import { vehicleLabel } from './Modules/vehicleLabel.js';

installAdminHistoryTracker();
initTheme();

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import { createPinia } from 'pinia';
import Popper from 'vue3-popper';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';

/* Import Font Awesome Component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

//Default Layout File
import DefaultLayoutFile from './Layouts/AppLayout.vue';
import ShopManagementLayoutFile from './Layouts/ShopManagement/AppShell.vue';

//Notify
import { Notyf } from 'notyf';
import { reuseSuccessConfirmations } from './Modules/SuccessConfirmations.js';

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'AutoFlow';
const configuredTheme = document.documentElement.dataset.uiTheme || import.meta.env.VITE_UI_THEME;
window.notyf = reuseSuccessConfirmations(
  new Notyf({
    types: [{ type: 'warning', background: '#f0ad4e', icon: false }],
  }),
);

const allowedStages = ['production', 'qc'];
const BUGSNAG_API_KEY = import.meta.env.VITE_BUGSNAG_API_KEY;
const ENTRY_BRAND = INITIAL_DOCUMENT_BRAND;
const IS_BAYHIVE_ENTRY = ENTRY_BRAND === 'bayhive';
if (IS_BAYHIVE_ENTRY) installEmbeddedAutoflowDialogSizing();
const BAYHIVE_FORM_ERROR_NAVIGATION_PT = IS_BAYHIVE_ENTRY
  ? installBayhiveFormErrorNavigation()
  : {};

initBayhiveFavicon();

const BAYHIVE_PRIMEVUE_PT = createBayhivePrimeVuePassThrough(BAYHIVE_FORM_ERROR_NAVIGATION_PT);

createInertiaApp({
  title: (title) => `${title} - ${activeBrand.value.id === ENTRY_BRAND ? appName : activeBrand.value.productName}`,
  resolve: async (name) => {
    const page = await resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob('./Pages/**/*.vue'),
    );
    page.default.layout ??=
      IS_BAYHIVE_ENTRY && name.startsWith('ShopManagement/')
        ? ShopManagementLayoutFile
        : DefaultLayoutFile;
    return page;
  },
  setup({ el, App, props, plugin }) {
    syncDocumentUiBrand(ENTRY_BRAND);
    const initialPreset = selectPageTheme(ENTRY_BRAND, props.initialPage.props.uiTheme || configuredTheme, props.initialPage.props.shopBrandTheme);
    const app = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(createPinia())
      .use(PrimeVue, {
        license: window.primeUiConfig?.license ?? '',
        ...(IS_BAYHIVE_ENTRY
          ? {
              pt: BAYHIVE_PRIMEVUE_PT,
              zIndex: {
                overlay: 4600,
                menu: 4000,
                modal: 4500,
                tooltip: 5000,
              },
            }
          : {}),
        theme: {
          preset: initialPreset,
          options: {
            darkModeSelector: '[data-bs-theme="dark"]',
            cssLayer: false,
          },
        },
      })
      .use(ConfirmationService)
      .use(ToastService)
      .use(ZiggyVue)
      .component('Popper', Popper)
      .component('FontAwesomeIcon', FontAwesomeIcon)
      .directive('bayhive-datatable-overflow', dataTableOverflow);

    // "Year Make Model Submodel" for templates in either app: {{ $vehicleLabel(vehicle) }}.
    app.config.globalProperties.$vehicleLabel = vehicleLabel;

    router.on('navigate', (event) => {
      const nextBrand = event.detail.page.props.uiBrand || ENTRY_BRAND;

      if (nextBrand === ENTRY_BRAND) {
        // Customer Share owns its snapshot theme, independently of staff UI branding.
        if (IS_BAYHIVE_ENTRY && event.detail.page.component === 'ShopManagement/CustomerShare')
          return;
        updatePageTheme(ENTRY_BRAND, event.detail.page.props.uiTheme || configuredTheme, event.detail.page.props.shopBrandTheme);
        return;
      }

      // A hard reload switches shells when the brand actually changes. Guard
      // against an infinite loop: if we already reloaded for this exact URL and
      // the brand still mismatches (e.g. an error page), render it instead of
      // reloading again.
      const url = event.detail.page.url;
      const guardKey = 'ui-brand-reload';
      try {
        if (window.sessionStorage.getItem(guardKey) === url) {
          window.sessionStorage.removeItem(guardKey);
          return;
        }
        window.sessionStorage.setItem(guardKey, url);
      } catch {
        // sessionStorage unavailable — fall through to a single reload.
      }
      window.location.assign(url);
    });

    const env = props.initialPage.props.appEnv; // or usePage().props.appEnv if already mounted
    const appVersion = props.initialPage.props.appVersion;

    if (allowedStages.includes(env) && BUGSNAG_API_KEY !== undefined) {
      Bugsnag.start({
        apiKey: BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        releaseStage: env,
        appVersion: appVersion || undefined,
      });

      const bugsnagVue = Bugsnag.getPlugin('vue');
      if (bugsnagVue) {
        bugsnagVue.installVueErrorHandler(app);
      }
    }

    return app.mount(el);
  },
  progress: {
    // The delay after which the progress bar will appear
    // during navigation, in milliseconds.
    delay: 250,

    // The color of the progress bar.
    color: IS_BAYHIVE_ENTRY ? '#4f2d7f' : '#29d',

    // Whether to include the default NProgress styles.
    includeCSS: true,

    // Whether the NProgress spinner will be shown.
    showSpinner: false,
  },
});

import.meta.glob(['../img/**', '../icons/**', '../css/pdf/**']);
